import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Download, FileSpreadsheet, FileText, Loader2, ExternalLink, Share2, MessageCircle, Mail, Printer, BarChart3, PieChart, TrendingUp } from 'lucide-react';

export default function Reports() {
  const { token, user } = useAuth();
  const [downloading, setDownloading] = useState('');
  const [shareModal, setShareModal] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const downloadReport = async (endpoint, filename, type) => {
    setDownloading(type);
    try {
      const response = await fetch(`${API_URL}/api${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Error al descargar el reporte. Por favor intenta de nuevo.');
    } finally {
      setDownloading('');
    }
  };

  const viewReport = async (endpoint, type, isPdf = false) => {
    setDownloading(type + '_view');
    try {
      const response = await fetch(`${API_URL}/api${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      const mimeType = isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      
      // For PDFs, try to open in new tab, fallback to download
      if (isPdf) {
        const newWindow = window.open(url, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Popup blocked, download instead
          const a = document.createElement('a');
          a.href = url;
          a.download = `M3_Reporte.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } else {
        // For Excel, always download since browsers can't display them
        const a = document.createElement('a');
        a.href = url;
        a.download = `M3_Reporte.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('View error:', err);
      alert('Error al abrir el reporte. Por favor intenta de nuevo.');
    } finally {
      setDownloading('');
    }
  };

  const shareReport = async (report, method) => {
    const reportName = report.title;
    const shareText = `M3 Gastronomic - ${reportName}\nGenerado el ${new Date().toLocaleDateString('es-ES')}`;
    
    if (method === 'whatsapp') {
      setDownloading(report.id + '_share');
      try {
        // Download the report first
        const response = await fetch(`${API_URL}/api${report.endpoint}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Trigger download
          const a = document.createElement('a');
          a.href = url;
          a.download = report.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        
        // Open WhatsApp
        const whatsappText = encodeURIComponent(`${shareText}\n\nDescarga tu reporte desde M3 Gastronomic`);
        window.open(`https://wa.me/?text=${whatsappText}`, '_blank');
      } catch (err) {
        console.error('Share error:', err);
        // Still try to open WhatsApp even if download fails
        const whatsappText = encodeURIComponent(`${shareText}\n\nAccede a M3 Gastronomic para ver el reporte`);
        window.open(`https://wa.me/?text=${whatsappText}`, '_blank');
      } finally {
        setDownloading('');
      }
    } else if (method === 'email') {
      const subject = encodeURIComponent(`M3 Gastronomic - ${reportName}`);
      const body = encodeURIComponent(`${shareText}\n\nAccede a M3 Gastronomic para descargar el reporte completo.`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } else if (method === 'print') {
      viewReport(report.endpoint, report.id, report.isPdf);
    }
    
    setShareModal(null);
  };

  const reports = [
    { 
      id: 'ingredients', 
      icon: FileSpreadsheet, 
      chartIcon: BarChart3,
      title: 'Reporte de Ingredientes', 
      desc: 'Base de datos completa con precios, merma, costos reales y graficos de distribucion por categoria',
      features: ['Grafico de costos', 'Distribucion por categoria', 'KPIs ejecutivos'],
      endpoint: '/reports/ingredients/excel', 
      filename: 'M3_Ingredientes.xlsx', 
      format: 'Excel',
      isPdf: false,
      color: 'emerald'
    },
    { 
      id: 'recipes', 
      icon: FileSpreadsheet, 
      chartIcon: PieChart,
      title: 'Fichas Tecnicas de Recetas', 
      desc: 'Todas las recetas con desglose de costos, food cost y analisis de rentabilidad',
      features: ['Food Cost por receta', 'Estado de rentabilidad', 'Precios sugeridos'],
      endpoint: '/reports/recipes/excel', 
      filename: 'M3_Recetas.xlsx', 
      format: 'Excel',
      isPdf: false,
      color: 'blue'
    },
    { 
      id: 'dashboard', 
      icon: FileSpreadsheet, 
      chartIcon: TrendingUp,
      title: 'Resumen Ejecutivo', 
      desc: 'Dashboard completo con KPIs clave, indicadores de rendimiento y metricas de alta gerencia',
      features: ['6 KPIs principales', 'Indicadores de estado', 'Alertas activas'],
      endpoint: '/reports/dashboard/excel', 
      filename: 'M3_Dashboard.xlsx', 
      format: 'Excel',
      isPdf: false,
      color: 'purple'
    },
    { 
      id: 'menu-excel', 
      icon: FileSpreadsheet, 
      chartIcon: PieChart,
      title: 'Menu Engineering Excel', 
      desc: 'Analisis de rentabilidad con clasificacion BCG, margenes y sugerencias estrategicas',
      features: ['Matriz BCG', 'Grafico de distribucion', 'Acciones sugeridas'],
      endpoint: '/reports/menu-engineering/excel', 
      filename: 'M3_MenuEngineering.xlsx', 
      format: 'Excel',
      isPdf: false,
      color: 'amber'
    },
    { 
      id: 'menu-pdf', 
      icon: FileText, 
      chartIcon: BarChart3,
      title: 'Menu Engineering PDF', 
      desc: 'Reporte visual profesional de la matriz BCG listo para imprimir y presentar',
      features: ['Formato ejecutivo', 'Leyenda de categorias', 'Listo para imprimir'],
      endpoint: '/reports/menu-engineering/pdf', 
      filename: 'M3_MenuEngineering.pdf', 
      format: 'PDF',
      isPdf: true,
      color: 'rose'
    },
  ];

  const colorClasses = {
    emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 hover:border-emerald-400',
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 hover:border-blue-400',
    purple: 'from-purple-500/20 to-purple-600/5 border-purple-500/30 hover:border-purple-400',
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/30 hover:border-amber-400',
    rose: 'from-rose-500/20 to-rose-600/5 border-rose-500/30 hover:border-rose-400',
  };

  const iconColors = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="text-center mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Reportes & Exportacion</p>
        <h2 className="font-heading text-4xl font-bold mb-2">Centro de Reportes</h2>
        <p className="text-sm text-white/50 max-w-xl mx-auto">
          Genera reportes profesionales con graficos, KPIs y analisis detallado. Perfectos para presentaciones ejecutivas.
        </p>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="reports-grid">
        {reports.map((r) => (
          <div 
            key={r.id} 
            className={`relative overflow-hidden bg-gradient-to-br ${colorClasses[r.color]} border transition-all duration-300 group`}
            data-testid={`report-card-${r.id}`}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-8 -translate-y-8">
              <r.chartIcon size={128} />
            </div>
            
            <div className="relative p-6">
              {/* Header */}
              <div className="flex items-start gap-4 mb-4">
                <div className={`p-3 rounded-lg bg-black/30 ${iconColors[r.color]}`}>
                  <r.icon size={24} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-base font-semibold">{r.title}</h4>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${r.format === 'PDF' ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                      {r.format}
                    </span>
                  </div>
                  <p className="text-xs text-white/50 leading-relaxed">{r.desc}</p>
                </div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-5">
                {r.features.map((f, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 bg-white/5 border border-white/10 text-white/60">
                    {f}
                  </span>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                <button 
                  onClick={() => viewReport(r.endpoint, r.id, r.isPdf)} 
                  disabled={downloading === r.id + '_view'} 
                  data-testid={`view-${r.id}`}
                  className="flex items-center gap-2 px-4 py-2.5 border border-white/20 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-all disabled:opacity-50"
                >
                  {downloading === r.id + '_view' ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                  Ver
                </button>
                
                <button 
                  onClick={() => downloadReport(r.endpoint, r.filename, r.id)} 
                  disabled={downloading === r.id} 
                  data-testid={`download-${r.id}`}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gold text-black text-xs uppercase tracking-widest hover:bg-gold/90 transition-all disabled:opacity-50 font-bold"
                >
                  {downloading === r.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  Descargar
                </button>

                <div className="relative">
                  <button 
                    onClick={() => setShareModal(shareModal === r.id ? null : r.id)}
                    data-testid={`share-${r.id}`}
                    className="flex items-center gap-2 px-3 py-2.5 border border-white/10 text-xs hover:border-white/30 transition-all"
                  >
                    <Share2 size={14} />
                  </button>
                  
                  {shareModal === r.id && (
                    <div className="absolute right-0 top-12 z-30 bg-[#0A0A0A] border border-white/10 py-2 min-w-[160px] shadow-xl">
                      <button 
                        onClick={() => shareReport(r, 'whatsapp')} 
                        className="w-full text-left px-4 py-2.5 text-xs hover:bg-white/5 flex items-center gap-3"
                      >
                        <MessageCircle size={14} className="text-emerald-400" /> 
                        WhatsApp
                      </button>
                      <button 
                        onClick={() => shareReport(r, 'email')} 
                        className="w-full text-left px-4 py-2.5 text-xs hover:bg-white/5 flex items-center gap-3"
                      >
                        <Mail size={14} className="text-blue-400" /> 
                        Email
                      </button>
                      <button 
                        onClick={() => shareReport(r, 'print')} 
                        className="w-full text-left px-4 py-2.5 text-xs hover:bg-white/5 flex items-center gap-3"
                      >
                        <Printer size={14} className="text-amber-400" /> 
                        Imprimir
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Print Info */}
      <div className="bg-gradient-to-r from-gold/5 to-transparent border border-gold/20 p-6" data-testid="print-info">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gold/10 rounded-lg">
            <Printer size={24} className="text-gold" />
          </div>
          <div>
            <h4 className="text-sm font-semibold mb-2">Sistema de Impresion Profesional</h4>
            <p className="text-xs text-white/50 leading-relaxed max-w-2xl">
              Todos los reportes estan optimizados para impresion a color con formato ejecutivo de alta gerencia. 
              Usa el boton "Ver" para abrir en nueva pestana y luego <strong className="text-white/70">Ctrl+P</strong> (Windows) o <strong className="text-white/70">Cmd+P</strong> (Mac) para imprimir.
              Los graficos y tablas mantienen su formato profesional al imprimir.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-[#0A0A0A] border border-white/5 text-center">
          <p className="text-2xl font-bold text-gold mb-1">{reports.length}</p>
          <p className="text-[10px] uppercase tracking-widest text-white/40">Tipos de Reporte</p>
        </div>
        <div className="p-4 bg-[#0A0A0A] border border-white/5 text-center">
          <p className="text-2xl font-bold text-emerald-400 mb-1">Excel</p>
          <p className="text-[10px] uppercase tracking-widest text-white/40">Con Graficos</p>
        </div>
        <div className="p-4 bg-[#0A0A0A] border border-white/5 text-center">
          <p className="text-2xl font-bold text-blue-400 mb-1">PDF</p>
          <p className="text-[10px] uppercase tracking-widest text-white/40">Listos para Imprimir</p>
        </div>
        <div className="p-4 bg-[#0A0A0A] border border-white/5 text-center">
          <p className="text-2xl font-bold text-purple-400 mb-1">Pro</p>
          <p className="text-[10px] uppercase tracking-widest text-white/40">Formato Ejecutivo</p>
        </div>
      </div>
    </div>
  );
}
